import { useCallback, useEffect, useState } from 'react'

export type UseTableRowSelectionOptions = {
    fetchAllIds: () => Promise<string[]>
    resetKey?: React.Key
}

export type UseTableRowSelectionResult = {
    selectedIds: Set<string>
    handleCheckboxChange: (id: string, checked: boolean) => void
    handleSelectAll: (checked: boolean) => Promise<void>
    allSelected: boolean
    someSelected: boolean
    isSelectingAll: boolean
}

export const useTableRowSelection = ({
    fetchAllIds,
    resetKey,
}: UseTableRowSelectionOptions): UseTableRowSelectionResult => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [totalSelectableCount, setTotalSelectableCount] = useState<number | null>(null)
    const [isSelectingAll, setIsSelectingAll] = useState(false)

    useEffect(() => {
        setSelectedIds(new Set())
        setTotalSelectableCount(null)
    }, [resetKey])

    const handleCheckboxChange = useCallback((id: string, checked: boolean) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev)
            if (checked) {
                newSet.add(id)
            } else {
                newSet.delete(id)
            }
            return newSet
        })
    }, [])

    const handleSelectAll = useCallback(
        async (checked: boolean) => {
            if (!checked) {
                setSelectedIds(new Set())
                setTotalSelectableCount(null)
                return
            }
            setIsSelectingAll(true)
            try {
                const ids = await fetchAllIds()
                const idSet = new Set(ids)
                setSelectedIds(idSet)
                setTotalSelectableCount(idSet.size)
            } finally {
                setIsSelectingAll(false)
            }
        },
        [fetchAllIds]
    )

    const allSelected =
        isSelectingAll ||
        (totalSelectableCount != null && totalSelectableCount > 0 && selectedIds.size === totalSelectableCount)
    const someSelected = selectedIds.size > 0

    return {
        selectedIds,
        handleCheckboxChange,
        handleSelectAll,
        allSelected,
        someSelected,
        isSelectingAll,
    }
}
